import { useRef, useEffect, useCallback } from 'react'
import { isTextSelection } from '@tiptap/core'
import type { Editor } from '@tiptap/react'
import type { BubbleMenuProps } from '@tiptap/react/menus'
import type { EditorView } from '@tiptap/pm/view'
import type { EditorState } from '@tiptap/pm/state'

interface UseBubbleMenuControllerProps {
  editor: Editor | null
  isEditingLink: boolean
}

/**
 * Hook to manage bubble menu visibility and pointer tracking
 * Handles complex logic for showing/hiding the bubble menu during text selection
 */
export const useBubbleMenuController = ({
  editor,
  isEditingLink,
}: UseBubbleMenuControllerProps) => {
  const isPointerDownRef = useRef(false)
  const pointerUpTimeoutRef = useRef<number | null>(null)

  // Setup pointer event listeners for bubble menu behavior
  useEffect(() => {
    if (!editor) {
      return
    }

    const editorDOM = editor.view.dom

    const handlePointerDown = () => {
      isPointerDownRef.current = true
      // Add selecting class to body to hide bubble menu during selection
      document.body.classList.add('editor-selecting')
    }

    const handlePointerUp = () => {
      if (!isPointerDownRef.current) {
        return
      }

      // Clear any pending timeout
      if (pointerUpTimeoutRef.current !== null) {
        window.clearTimeout(pointerUpTimeoutRef.current)
      }

      // Remove selecting class immediately to show bubble menu
      document.body.classList.remove('editor-selecting')

      // Add small delay to let selection finalize before updating bubble menu position
      pointerUpTimeoutRef.current = window.setTimeout(() => {
        isPointerDownRef.current = false

        if (editor.isDestroyed) {
          return
        }

        const { state, view } = editor
        const transaction = state.tr.setMeta('bubbleMenu', 'updatePosition')
        view.dispatch(transaction)
      }, 50)
    }

    editorDOM.addEventListener('pointerdown', handlePointerDown)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerUp)

    return () => {
      editorDOM.removeEventListener('pointerdown', handlePointerDown)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerUp)

      // Clear timeout on cleanup
      if (pointerUpTimeoutRef.current !== null) {
        window.clearTimeout(pointerUpTimeoutRef.current)
      }

      // Remove selecting class from body on cleanup
      document.body.classList.remove('editor-selecting')
    }
  }, [editor])

  const shouldShow = useCallback<NonNullable<BubbleMenuProps['shouldShow']>>(
    ({ editor: bubbleEditor, view, state, from, to }: {
      editor: Editor;
      view: EditorView;
      state: EditorState;
      from: number;
      to: number
    }) => {
      if (!bubbleEditor.isEditable) {
        return false
      }

      if (!view.hasFocus() && !isEditingLink) {
        return false
      }

      const { selection, doc } = state

      if (selection.empty && !isEditingLink) {
        return false
      }

      const isEmptyTextBlock = !doc.textBetween(from, to).length && isTextSelection(selection)

      if (isEmptyTextBlock && !isEditingLink) {
        return false
      }

      return true
    },
    [isEditingLink],
  )

  return { shouldShow }
}
